import { resolveDroneCategory } from "@nova/constants"
import { CurrencyNotificationKind, CurrencyUserFlightRow } from "@nova/schemas"

const DAY_MS = 24 * 3600 * 1000

export type CurrencyMetric = "flights" | "hours"

export type CurrencyState = "current" | "upcoming" | "overdue"

export type CurrencyFlight = {
    startedAt: number
    airtimeSeconds: number
    manufacturer: string | null
    model: string | null
}

export const currencyFlightFromRow = (row: CurrencyUserFlightRow): CurrencyFlight => ({
    startedAt: new Date(row.started_at).getTime(),
    airtimeSeconds: row.airtime ?? 0,
    manufacturer: row.manufacturer,
    model: row.model,
})

export type CurrencyPolicyInput = {
    metric: CurrencyMetric
    threshold: number
    windowDays: number
    warningDays: number
    droneTypes: string[]
    assignedAt: number
}

export type CurrencyStatus = {
    progress: number
    state: CurrencyState
    daysLeft: number | null
    // When current/upcoming: the UTC-day-floored timestamp the pilot will lapse.
    // When overdue: the UTC-day-floored timestamp they DID lapse (their most recent credit
    // aged out). Either way it is a stable per-lapse anchor — never derived from "now" — so it
    // serves as the dedup identity for notifications and does not drift day to day.
    expiresAt: number | null
    // When upcoming: how much credit is short once the soonest-expiring credit (the one at
    // expiresAt) ages out of the window — i.e. what's still needed to stay current past that
    // date. Always 0 for current/overdue.
    shortfallAtExpiry: number
}

const floorToDay = (ms: number) => Math.floor(ms / DAY_MS) * DAY_MS

const flightMatchesPolicy = (flight: CurrencyFlight, droneTypes: string[]): boolean => {
    if (droneTypes.length === 0) return true
    const category = resolveDroneCategory(flight.manufacturer, flight.model)
    return category != null && droneTypes.includes(category)
}

export const computeCurrencyStatus = (
    policy: CurrencyPolicyInput,
    flights: CurrencyFlight[],
    now: number
): CurrencyStatus => {
    const windowMs = policy.windowDays * DAY_MS
    const cutoff = now - windowMs

    const credit = (f: CurrencyFlight) => (policy.metric === "hours" ? f.airtimeSeconds / 3600 : 1)

    const matching = flights.filter((f) => flightMatchesPolicy(f, policy.droneTypes))
    const inWindow = matching.filter((f) => f.startedAt > cutoff)

    // The assignment date contributes a synthetic full-threshold credit so a pilot is
    // "current" from assignment until that credit ages out of the window. A pilot with no
    // real flights goes overdue exactly at assignedAt + window.
    const events: { date: number; value: number }[] = inWindow.map((f) => ({
        date: f.startedAt,
        value: credit(f),
    }))
    if (policy.assignedAt > cutoff) {
        events.push({ date: policy.assignedAt, value: policy.threshold })
    }

    const total = events.reduce((sum, e) => sum + e.value, 0)
    const rawProgress = Math.min(total, policy.threshold)
    const progress = policy.metric === "hours" ? Number(rawProgress.toFixed(1)) : rawProgress
    if (total < policy.threshold) {
        // Overdue: anchor on the date they dropped below threshold — their most recent credit
        // (or the assignment credit) aging out. Uses all matching flights, not just in-window,
        // since the credit that lapsed them may already have aged out. Deterministic per lapse,
        // independent of "now".
        const mostRecentCredit = matching.reduce(
            (max, f) => (f.startedAt > max ? f.startedAt : max),
            policy.assignedAt
        )
        return {
            progress,
            state: "overdue",
            daysLeft: null,
            expiresAt: floorToDay(mostRecentCredit + windowMs),
            shortfallAtExpiry: 0,
        }
    }

    // Walk newest-first, keeping just enough credit to meet the threshold. The oldest credit
    // we must keep determines when we drop below threshold — that credit's date + window.
    const desc = events.toSorted((a, b) => b.date - a.date)
    let kept = 0
    let oldestNeeded = now
    for (const e of desc) {
        kept += e.value
        oldestNeeded = e.date
        if (kept >= policy.threshold) break
    }

    // Floor to the UTC day so expiresAt is a stable, day-granular identity for notification
    // dedup — it must not jitter on sub-day differences in flight timestamps.
    const expiresAt = floorToDay(oldestNeeded + windowMs)
    const daysLeft = Math.max(0, Math.ceil((expiresAt - now) / DAY_MS))
    const state: CurrencyState = daysLeft <= policy.warningDays ? "upcoming" : "current"
    // Credit sharing the exact date of the oldest kept event ages out simultaneously — sum
    // it, not just one event, so simultaneous credit isn't undercounted.
    const expiringValue = desc
        .filter((e) => e.date === oldestNeeded)
        .reduce((sum, e) => sum + e.value, 0)
    const shortfallAtExpiry = Math.max(0, policy.threshold - (kept - expiringValue))
    return { progress, state, daysLeft, expiresAt, shortfallAtExpiry }
}

export type CurrencyStatesSummary = {
    state: CurrencyState
    daysLeft: number | null
}

export const summarizeCurrencyStates = (
    statuses: { state: CurrencyState; daysLeft: number | null }[]
): CurrencyStatesSummary => {
    if (statuses.some((s) => s.state === "overdue")) {
        return { state: "overdue", daysLeft: null }
    }
    const upcoming = statuses.filter((s) => s.state === "upcoming")
    if (upcoming.length > 0) {
        return { state: "upcoming", daysLeft: Math.min(...upcoming.map((s) => s.daysLeft ?? 0)) }
    }
    return { state: "current", daysLeft: null }
}

export type CurrencyNotificationDecision = {
    kind: CurrencyNotificationKind
    expiresAtMs: number
    expiresAt: string
    daysLeft: number | null
    remaining: number
    expirationDate: string
}

const formatExpirationDate = (ms: number) =>
    new Date(ms).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    })

// Only "upcoming" (warning window) and "overdue" trigger emails. expiresAt is a stable
// per-lapse timestamp from computeCurrencyStatus (never "now"), so it doubles as the dedup
// anchor: re-sends within a lapse share an identity, and a new lapse against a new window is
// a new identity that re-fires. Returns null when no notification is due.
export const evaluateCurrencyNotification = (
    policy: CurrencyPolicyInput,
    flights: CurrencyFlight[],
    now: number
): CurrencyNotificationDecision | null => {
    const status = computeCurrencyStatus(policy, flights, now)

    const kind: CurrencyNotificationKind | null =
        status.state === "upcoming" ? "warning" : status.state === "overdue" ? "overdue" : null
    if (!kind || status.expiresAt == null) return null

    const remaining = Math.round(status.shortfallAtExpiry * 10) / 10

    return {
        kind,
        expiresAtMs: status.expiresAt,
        expiresAt: new Date(status.expiresAt).toISOString(),
        daysLeft: status.daysLeft,
        remaining,
        expirationDate: formatExpirationDate(status.expiresAt),
    }
}
